import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useOsLicense } from '@/hooks/supabase/useOsLicense';
import { useLicenseActivations } from '@/hooks/supabase/useLicenseActivations';

export type MarketplaceReviewAction = 'approve' | 'reject' | 'publish' | 'unpublish' | 'hide';

export function useMarketplacePublishWorkflow(isEmbeddedBuild = true) {
  const { session } = useAuth();
  const { hasProOrEnterprise } = useOsLicense();
  const {
    currentDeviceActivated,
    loading: activationLoading,
    actionLoading: activationActionLoading,
  } = useLicenseActivations({ autoActivate: false });
  const [submitting, setSubmitting] = useState(false);

  const callWithAuth = useCallback(async (fn: () => Promise<{ data: any; error: any }>) => {
    if (!session?.access_token) {
      throw new Error('You must be signed in to perform this action.');
    }

    setSubmitting(true);
    try {
      const result = await fn();
      if (result.error || result.data?.error) {
        throw new Error(result.error?.message || result.data?.error || 'Request failed.');
      }
      return result.data;
    } finally {
      setSubmitting(false);
    }
  }, [session?.access_token]);

  const requestPublish = useCallback(async (productId: string) => {
    if (!isEmbeddedBuild) {
      throw new Error('Publish requests must be submitted from a MapleOS Pro build.');
    }

    if (!hasProOrEnterprise) {
      throw new Error('MapleOS Pro or Enterprise is required to request publication.');
    }

    if (!currentDeviceActivated) {
      throw new Error('Activate this MapleOS Pro device before requesting publication.');
    }

    return callWithAuth(() =>
      supabase.functions.invoke('request-marketplace-publish', {
        body: { productId },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      }),
    );
  }, [callWithAuth, currentDeviceActivated, hasProOrEnterprise, isEmbeddedBuild, session?.access_token]);

  const reviewPublishRequest = useCallback(async (productId: string, action: MarketplaceReviewAction, reason?: string) => {
    return callWithAuth(() =>
      supabase.functions.invoke('review-marketplace-publish', {
        body: { productId, action, reason },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      }),
    );
  }, [callWithAuth, session?.access_token]);

  return {
    requestPublish,
    reviewPublishRequest,
    submitting,
    publishGateLoading: activationLoading || activationActionLoading,
    canRequestPublish: isEmbeddedBuild && hasProOrEnterprise && currentDeviceActivated,
    hasProOrEnterprise,
    currentDeviceActivated,
  };
}
